import { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";

import "react-datepicker/dist/react-datepicker.css";
import {
  createTheme,
  ThemeProvider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import {
  DEC_COLS,
  SETTLE_COLS,
  SETTLE_COLUMN_NAMES,
} from "../../../utils/constants";
import { LOCK_TOOLTIP_TEXT } from "../../../utils/constants/last-price";
import useLastPrice from "../../../hooks/useLastPrice";
import styles from "./styles.module.css";
import LastPriceHeader from "./header";
registerLocale("es", es);

function LastPriceTable(props) {
  const {
    data,
    databyCurve,
    startDate,
    setStartDate,
    customSettles,
    dropdownValue,
    isEditingLocked,
    fromMibgenerics,
    hasChanges,
    handleDropdownChange,
    handleInputChange,
    isSaving,
    handleClick,
    refresh,
    pendingConfirm,
    confirmSave,
    cancelSave,
    focusedEditableIndex,
  } = useLastPrice();

  const warning_dict = { leve: styles.warningLeve, grave: styles.warningGrave };
  const theme = createTheme();

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={Boolean(pendingConfirm)} onClose={cancelSave}>
        <DialogTitle>Confirmar guardado sin todos los contratos</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Los siguientes contratos no tienen precio anticipado asignado:
          </DialogContentText>
          <ul style={{ marginTop: 8 }}>
            {pendingConfirm?.missingLabels.map((label) => (
              <li key={label}>
                <strong>{label}</strong>
              </li>
            ))}
          </ul>
          <DialogContentText style={{ marginTop: 8 }}>
            Sin estos contratos, el tramo correspondiente de la curva PVB se
            extrapolará desde los contratos más cortos con precio. ¿Deseas
            guardar igualmente?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelSave} color="inherit">
            Cancelar
          </Button>
          <Button onClick={confirmSave} color="warning" variant="contained">
            Guardar de todos modos
          </Button>
        </DialogActions>
      </Dialog>
      <div className={styles.container}>
        <LastPriceHeader
          props={props}
          startDate={startDate}
          setStartDate={setStartDate}
          handleClick={handleClick}
          refresh={refresh}
          isEditingLocked={isEditingLocked}
          hasChanges={hasChanges}
          isSaving={isSaving}
        />

        {isEditingLocked ? (
          <div className={styles.lockMessage}>
            <p className={styles.lockMessageText}>{LOCK_TOOLTIP_TEXT}</p>
          </div>
        ) : null}

        <div className={styles.tablesContainer}>
          {databyCurve.size === 0 ? (
            <div className={styles.noDataMessage}>
              <h5>No data for this day</h5>
            </div>
          ) : null}
          {[...databyCurve.entries()].map(([curvekey, dataCurve]) => {
            return (
              <div className={styles.tableSection} key={curvekey}>
                <h4 className={styles.curveTitle}>{curvekey}</h4>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr className={styles.tableHeader}>
                        {Object.keys(SETTLE_COLUMN_NAMES).map((key) => (
                          <th className={styles.tableHeaderCell} key={key}>
                            {SETTLE_COLUMN_NAMES[key]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dataCurve.map((row, index) => (
                        <tr
                          key={`${row.id ?? "row"}-${index}`}
                          className={`${styles.tableRow} ${index % 2 === 1 ? styles.tableRowAlt : ""}`}
                        >
                          {Object.entries(row).map(([key, cell]) => {
                            if (key === "settle_reference_time") {
                              if (cell != null) {
                                let date = new Date(cell);
                                return (
                                  <td
                                    className={styles.tableCell}
                                    key={`${row.id ?? index}-settle_reference_time`}
                                  >
                                    {date.toLocaleTimeString("es-ES", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: false,
                                    })}
                                  </td>
                                );
                              } else {
                                return (
                                  <td
                                    className={styles.tableCell}
                                    key={`${row.id ?? index}-settle_reference_time`}
                                  />
                                );
                              }
                            } else if (key === "mibeod_warnings") {
                              return null;
                            } else if (key === "settle_last_modification") {
                              if (cell != null) {
                                let date = new Date(cell);
                                return (
                                  <td
                                    className={styles.tableCell}
                                    key={`${row.id ?? index}-${key}`}
                                  >
                                    {date.toLocaleString("es-ES", {})}
                                  </td>
                                );
                              } else {
                                return (
                                  <td
                                    className={styles.tableCell}
                                    key={`${row.id ?? index}-${key}`}
                                  />
                                );
                              }
                            } else if (key === "settle_modified") {
                              // let content = cell === null ? '' : cell
                              let nonnullcols = SETTLE_COLS.filter(
                                (column) => row[column] !== null
                              );
                              let index = data.findIndex(
                                (dict) => dict.instrument === row.instrument
                              );
                              const hasStoredAnticipatedPrice =
                                row.settle_modified !== null;
                              const anticipatedLabelValue =
                                customSettles.hasOwnProperty(index) &&
                                dropdownValue[index] === "anticipated"
                                  ? customSettles[index]
                                  : hasStoredAnticipatedPrice
                                    ? row.settle_modified
                                    : "";
                              return (
                                <td
                                  className={styles.tableCell}
                                  key={`${row.id ?? index}-${key}`}
                                >
                                  {/* <input className="text-center" value={content} onChange={(e) => handleChange(e, key, index)} /> */}
                                  <div>
                                    <span
                                      title={
                                        isEditingLocked ? LOCK_TOOLTIP_TEXT : ""
                                      }
                                    >
                                      <select
                                        className={styles.dropdown}
                                        value={
                                          dropdownValue.hasOwnProperty(index)
                                            ? dropdownValue[index]
                                            : ""
                                        }
                                        onChange={(e) =>
                                          handleDropdownChange(e, index)
                                        }
                                        disabled={isEditingLocked}
                                      >
                                        <option value="" disabled hidden>
                                          Modificar
                                        </option>
                                        {nonnullcols.map((column) => (
                                          <option
                                            key={SETTLE_COLUMN_NAMES[column]}
                                            value={column}
                                          >
                                            {SETTLE_COLUMN_NAMES[column]}:{" "}
                                            {row[column]}
                                          </option>
                                        ))}
                                        <option value="custom">
                                          Modificar{" "}
                                          {customSettles.hasOwnProperty(index)
                                            ? customSettles[index]
                                            : ""}
                                        </option>
                                        {(fromMibgenerics ||
                                          hasStoredAnticipatedPrice) && (
                                          <option value="anticipated">
                                            Precio anticipado{" "}
                                            {anticipatedLabelValue}
                                          </option>
                                        )}
                                        <option value="remove">Eliminar</option>
                                      </select>
                                    </span>
                                    {dropdownValue.hasOwnProperty(index) &&
                                      (dropdownValue[index] === "custom" ||
                                        dropdownValue[index] ===
                                          "anticipated") && (
                                        <input
                                          type="number"
                                          name={index}
                                          autoFocus={
                                            focusedEditableIndex === index
                                          }
                                          value={
                                            customSettles.hasOwnProperty(index)
                                              ? customSettles[index]
                                              : ""
                                          }
                                          onChange={(e) =>
                                            handleInputChange(e, index)
                                          }
                                          disabled={isEditingLocked}
                                        />
                                      )}
                                  </div>
                                </td>
                              );
                            } else {
                              let classname;
                              let title;
                              if (
                                ["instrument", "settle"].includes(key) &&
                                row.mibeod_warnings !== null
                              ) {
                                classname = `${styles.tableCell} ${warning_dict[row.mibeod_warnings]}`;
                                title = `Nivel de Aviso: ${row.mibeod_warnings}`;
                              } else {
                                classname = styles.tableCell;
                                title = "";
                              }
                              return (
                                <td
                                  className={classname}
                                  title={title}
                                  key={`${row.id ?? index}-${key}`}
                                >
                                  {DEC_COLS.includes(key) && parseFloat(cell)
                                    ? parseFloat(cell).toFixed(3)
                                    : cell}
                                </td>
                              );
                            }
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ThemeProvider>
  );
}

export default LastPriceTable;
